import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItemConfirm,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTeams } from "@/contexts/teams-provider";
import { useUser } from "@/contexts/user-provider";
import { useUsers } from "@/contexts/users-provider";
import { TeamUserRolesEnum } from "@/enums/team-user-role";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { TeamUserDTO } from "@shaarp/common";
import { ChevronDownIcon } from "lucide-react";
import { Button } from "../ui/button";
import { useToastError } from "../ui/use-toast";

export default function DataTableCellUserActions({
  user,
}: {
  user: TeamUserDTO;
}) {
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();
  const { isAdmin, isManager } = useTeams();
  const { teamUser: currentTeamUser } = useUser();

  const { apiFetchUsers } = useUsers();

  // Check if target user is the current logged-in user
  const isCurrentUser =
    currentTeamUser?.id &&
    user.id &&
    String(currentTeamUser.id) === String(user.id);

  // Admin can remove anyone including self (even other Admins)
  // Manager can remove Members, Managers, and Advisors, but not Admins and themselves
  const hasPermission =
    isAdmin ||
    (isManager && user.role !== TeamUserRolesEnum.ADMIN && !isCurrentUser);

  const remove = async () => {
    try {
      await api.delete(`/my/team/user/${user.id}`);
      apiFetchUsers({ force: true });
    } catch (e) {
      toastError();
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          {t("components.dataTableUsers.actions.actions")}
          <ChevronDownIcon className="ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItemConfirm
          className={
            !hasPermission ? "opacity-50 cursor-not-allowed" : "text-red-500"
          }
          text={t("components.dataTableUsers.actions.confirmRemove")}
          description={t("components.dataTableUsers.actions.removeDescription")}
          onConfirm={() => remove()}
          disabled={!hasPermission}
        >
          {t("components.dataTableUsers.actions.removeFromTeam")}
        </DropdownMenuItemConfirm>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
